import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { deleteClientLevel, getClientLevels } from "../../api/Services";
import { hasPermission, privilegesEnum } from "../../helpers/PermissionHelper";
import {
  useDebouncedValue,
  useFetchRequestGuard,
} from "../../helpers/FilterSearchHelper";

import {
  Table,
  Button,
  message,
  Modal,
  Input,
  Dropdown,
  Menu,
  Tooltip,
  Select,
  Tag,
} from "antd";
import {
  DeleteOutlined,
  EditOutlined,
  ExclamationCircleOutlined,
  EyeOutlined,
  MoreOutlined,
  SearchOutlined,
  ClearOutlined,
} from "@ant-design/icons";
import { RxLayers } from "react-icons/rx";
import PageHeader from "../../components/pageheader/PageHeader";
import EmptyState from "../../components/emptystate/EmptyState";

import styles from "./ClientLevels.module.scss";
import { useNavigate, useLocation } from "react-router-dom";
import { useSearchParams } from "react-router-dom";

const ClientLevels = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const getBasePath = () => {
    if (location.pathname.startsWith("/commercial-intelligence")) {
      return "/commercial-intelligence/configuration/client-levels";
    }
    if (location.pathname.startsWith("/fidelity")) {
      return "/fidelity/clientlevels";
    }
    return "/promotions/clientlevels";
  };

  const [data, setData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalSize, setTotalSize] = useState(0);
  const [actualPage, setActualPage] = useState(1);
  const { beginRequest, isStaleRequest } = useFetchRequestGuard();
  const [nameFilterValue, setNameFilterValue] = useState(searchParams.get("name") || "");
  const debouncedNameFilterValue = useDebouncedValue(nameFilterValue);
  const [levelFilterValue, setLevelFilterValue] = useState(searchParams.get("level") || "");
  const debouncedLevelFilterValue = useDebouncedValue(levelFilterValue);
  const [originFilterValue, setOriginFilterValue] = useState(searchParams.get("origin") || "");

  useEffect(() => {
    if (hasPermission(privilegesEnum.READ_CLIENT_LEVEL)) {
      // Inicializar la página desde los parámetros de URL
      const pageFromUrl = searchParams.get("page");
      if (pageFromUrl) {
        setActualPage(parseInt(pageFromUrl));
      }
      fetchClientLevels();
    }
  }, [debouncedNameFilterValue, debouncedLevelFilterValue, originFilterValue, searchParams]);

  const fetchClientLevels = async (pagination = actualPage) => {
    const requestId = beginRequest();
    const response = await getClientLevels(pagination, {
      name: debouncedNameFilterValue,
      level: debouncedLevelFilterValue,
      origin: originFilterValue,
    });

    if (isStaleRequest(requestId)) return;

    setData(response.clientLevels);
    setTotalSize(response.totalElements);
    setLoading(false);
  };

  const showDeleteConfirm = (id: number) => {
    Modal.confirm({
      title: t("Common-DeleteConfirm"),
      icon: <ExclamationCircleOutlined />,
      content: "",
      okText: t("Common-Yes"),
      okType: "danger",
      cancelText: t("Common-No"),
      onOk() {
        handleDelete(id);
      },
    });
  };

  const handleDelete = async (id: number) => {
    setLoading(true);
    const deleteStatus = await deleteClientLevel(id);
    if (!deleteStatus) message.error(t("Common-DeleteErrorMessage"));
    else message.success(t("Common-DeleteSuccessMessage"));
    await fetchClientLevels();
    setLoading(false);
  };

  const updateURLParams = (filters: any) => {
    const params = new URLSearchParams();
    if (filters.name) params.set("name", filters.name);
    if (filters.level) params.set("level", filters.level);
    if (filters.origin) params.set("origin", filters.origin);
    if (filters.page && filters.page > 1) params.set("page", filters.page.toString());
    
    setSearchParams(params);
  };

  const handlePagination = (page: number) => {
    setActualPage(page);
    updateURLParams({
      name: nameFilterValue,
      level: levelFilterValue,
      origin: originFilterValue,
      page: page
    });
    fetchClientLevels(page);
  };

  const handleClearFilters = () => {
    setNameFilterValue("");
    setLevelFilterValue("");
    setOriginFilterValue("");
    setActualPage(1);
    setSearchParams(new URLSearchParams());
    fetchClientLevels(1);
  };

  const hasActiveFilters = () => {
    return nameFilterValue !== "" || levelFilterValue !== "" || originFilterValue !== "";
  };

  const hasNoData = () => {
    return !loading && data.length === 0;
  };

  const hasNoDataDueToFilters = () => {
    return hasNoData() && hasActiveFilters();
  };

  const hasNoDataAtAll = () => {
    return hasNoData() && !hasActiveFilters();
  };

  const getEmptyState = () => {
    if (hasNoDataDueToFilters()) {
      return <EmptyState type="noResults" entityName="ClientLevel" />;
    }

    if (hasNoDataAtAll()) {
      return (
        <EmptyState
          type="noData"
          entityName="ClientLevel"
          createPath={`${getBasePath()}/create`}
          createPermission={privilegesEnum.WRITE_CLIENT_LEVEL}
        />
      );
    }

    return null;
  };

  const columns = [
    {
      title: t("Common-Level"),
      dataIndex: "level",
    },
    {
      title: t("Common-Name"),
      dataIndex: "name",
      render: (name: string, record: any) => (
        <a
        href={`${getBasePath()}/${record.id}/view?${searchParams.toString()}`}
        className={styles.nameLink}
        >
          {name}
        </a>
      ),
    },
    {
      title: t("Common-Description"),
      dataIndex: "description",
    },
    {
      title: t("Common-TotalClients"),
      dataIndex: "totalClients",
    },
    {
      title: t("Common-AccumulationFactor"),
      dataIndex: "accumulationFactor",
    },
    {
      title: t("Common-CreatedDate"),
      dataIndex: "createdAt",
      render: (date: string) => date?.split("T")[0],
    },
    {
      title: t("Common-Origin"),
      dataIndex: "origin",
      render: (origin: any) => {
        if (!origin) return "-";
        const originValue = origin.value || origin;
        const isManual = originValue === "MANUAL";
        const isSuggested = originValue === "SUGGESTED";
        
        return (
          <Tag color={isManual ? "blue" : isSuggested ? "green" : "default"}>
            {isManual ? "Manual" : isSuggested ? "Sugerido" : originValue}
          </Tag>
        );
      },
    },
    {
      title: t("Common-Analysis"),
      dataIndex: "analysis",
    },
    {
      title: t("Common-Actions"),
      key: "action",
      render: (text: any, record: any) => (
        <Dropdown
          overlay={
            <Menu>
              <Menu.Item
                key="view"
                icon={<EyeOutlined />}
                onClick={() => navigate(`${getBasePath()}/${record.id}/view?${searchParams.toString()}`)}
              >
                {t("Common-View")}
              </Menu.Item>
              {hasPermission(privilegesEnum.WRITE_CLIENT_LEVEL) && (
                <Menu.Item
                  key="edit"
                  icon={<EditOutlined />}
                  onClick={() => navigate(`${getBasePath()}/${record.id}/view?${searchParams.toString()}`)}
                >
                  {t("Common-Edit")}
                </Menu.Item>
              )}
              {hasPermission(privilegesEnum.DELETE_CLIENT_LEVEL) && (
                <Menu.Item
                  key="delete"
                  icon={<DeleteOutlined />}
                  onClick={() => showDeleteConfirm(record.id)}
                >
                  {t("Common-Delete")}
                </Menu.Item>
              )}
            </Menu>
          }
        >
          <Button className={styles.actionButtonMore}>
            <MoreOutlined className={styles.actionIconMore} />
          </Button>
        </Dropdown>
      ),
    },
  ];

  return (
    <div className={styles.wrapper}>
      <PageHeader
        title={t("Common-ClientLevels")}
        icon={<RxLayers />}
        onCreateClick={() => navigate(`${getBasePath()}/create`)}
        createPermission={privilegesEnum.WRITE_CLIENT_LEVEL}
      />
      <div className={styles.contentWrapper}>
        {hasPermission(privilegesEnum.READ_CLIENT_LEVEL) && (
          <div className={styles.tableWrapper}>
            <div className={styles.headerTableWrapper}>
              <div className={styles.filtersWrapper}>
                <div className={styles.filterWrapper}>
                  <Input
                    placeholder={t("Common-SearchByName")}
                    className={styles.filterInput}
                    prefix={<SearchOutlined />}
                    allowClear
                    onChange={(e) => {
                      setNameFilterValue(e.target.value);
                      setActualPage(1);
                      updateURLParams({
                        name: e.target.value,
                        level: levelFilterValue,
                        origin: originFilterValue,
                        page: 1
                      });
                    }}
                    value={nameFilterValue}
                  />
                </div>
                <div className={styles.filterWrapper}>
                  <Input
                    placeholder={t("Common-SearchByLevel")}
                    className={styles.filterInput}
                    prefix={<SearchOutlined />}
                    allowClear
                    onChange={(e) => {
                      setLevelFilterValue(e.target.value);
                      setActualPage(1);
                      updateURLParams({
                        name: nameFilterValue,
                        level: e.target.value,
                        origin: originFilterValue,
                        page: 1
                      });
                    }}
                    value={levelFilterValue}
                  />
                </div>
                <div className={styles.filterWrapper}>
                  <Select
                    showSearch
                    allowClear
                    value={originFilterValue || undefined}
                    className={styles.filterSelect}
                    placeholder={t("Common-Origin")}
                    filterOption={(input: string, option: any) =>
                      option.children
                        .toLowerCase()
                        .indexOf(input.toLowerCase()) >= 0
                    }
                    onSelect={(_, option) => {
                      if (option) {
                        setOriginFilterValue(option.value);
                        setActualPage(1);
                        updateURLParams({
                          name: nameFilterValue,
                          level: levelFilterValue,
                          origin: option.value,
                          page: 1
                        });
                      }
                    }}
                    onClear={() => {
                      setOriginFilterValue("");
                      setActualPage(1);
                      updateURLParams({
                        name: nameFilterValue,
                        level: levelFilterValue,
                        origin: "",
                        page: 1
                      });
                    }}
                  >
                    <Select.Option value="MANUAL">
                      {t("Common-Manual")}
                    </Select.Option>
                    <Select.Option value="SUGGESTED">
                      {t("Common-Suggested")}
                    </Select.Option>
                  </Select>
                </div>
                {hasActiveFilters() && (
                  <div className={styles.filterWrapper}>
                    <Tooltip title={t("Common-Clear")}>
                      <Button
                        onClick={handleClearFilters}
                        className={styles.clearButton}
                        icon={<ClearOutlined />}
                      >
                        {t("Common-Clear")}
                      </Button>
                    </Tooltip>
                  </div>
                )}
              </div>
            </div>

            <Table
              size="small"
              rowKey={(record) => record.id}
              className={styles.table}
              loading={loading}
              columns={columns}
              dataSource={data}
              pagination={{
                total: totalSize,
                showSizeChanger: false,
                onChange: handlePagination,
                current: actualPage,
                showTotal: (total, range) =>
                  `${range[0]}-${range[1]} / ${total}`,
              }}
              locale={{
                emptyText: getEmptyState(),
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ClientLevels;
